import Phaser from 'phaser';
import { TARGET_FPS, UI } from './Constants';

/**
 * Phaser Game Configuration
 * 
 * UPDATED: Now supports both portrait AND landscape modes
 * - Uses RESIZE mode for full responsive scaling
 * - Adapts to any screen size (mobile, tablet, desktop)
 * - Maintains gameplay quality at any aspect ratio
 */



// Full HD Quality settings - always high quality for crisp visuals
const getQualitySettings = () => {
  // Always use high quality settings for Full HD rendering
  return {
    antialias: true,
    pixelArt: false,
    roundPixels: false,
    fps: TARGET_FPS
  };
};

const quality = getQualitySettings();

/**
 * Get responsive game dimensions based on viewport
 * - Portrait: Uses traditional 720x1280 ratio
 * - Landscape: Uses 1280x720 ratio for proper desktop display
 */
const getResponsiveDimensions = () => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // Always use actual viewport size - no artificial limits
  // This ensures full screen on ALL devices
  return {
    width: viewportWidth,
    height: viewportHeight
  };
};

const dimensions = getResponsiveDimensions();

/**
 * Main Phaser configuration object
 * Scenes are added dynamically in main.ts to avoid circular imports
 */
export const GameConfig: Phaser.Types.Core.GameConfig = {
  // Renderer settings - WebGL preferred with Canvas fallback
  type: Phaser.AUTO,
  
  // Parent container
  parent: 'game-container',
  
  // Background color
  backgroundColor: UI.BACKGROUND_COLOR,
  
  // Disable auto-rounding for smoother mobile rendering
  autoRound: false,
  
  // Scale configuration for FULL responsive design
  scale: {
    // RESIZE mode adapts to any viewport size
    mode: Phaser.Scale.RESIZE,
    
    // Auto-center the game
    autoCenter: Phaser.Scale.CENTER_BOTH,
    
    // Initial dimensions (will be overridden by RESIZE mode)
    width: dimensions.width,
    height: dimensions.height,
    
    // Expand to fill container
    expandParent: true,
    
    // Min/max constraints - very generous for all devices
    min: {
      width: 280,
      height: 400
    },
    max: {
      width: 2560,  // 2K support
      height: 2560  // Tablets in portrait
    }
  },
  
  // Full HD Rendering settings
  render: {
    // Smooth edges for Full HD quality
    antialias: true,
    
    // Disable pixel art mode for smooth graphics
    pixelArt: false,
    
    // Don't round pixels for smooth visuals
    roundPixels: false,
    
    // Transparent canvas (for custom backgrounds)
    transparent: false,
    
    // Clear before render
    clearBeforeRender: true,
    
    // Preserve drawing buffer for screenshots
    preserveDrawingBuffer: false,
    
    // Power preference for mobile
    powerPreference: 'high-performance',
    
    // Batch size for optimal rendering
    batchSize: 4096,
    
    // Max textures for high quality
    maxTextures: 8
  },
  
  // Physics configuration (using custom physics, not Arcade)
  physics: {
    default: 'arcade',
    arcade: {
      // Gravity is handled manually for orbital mechanics
      gravity: { x: 0, y: 0 },
      
      // Debug mode off in production
      debug: false
    }
  },
  
  // Frame rate settings
  fps: {
    target: quality.fps,
    forceSetTimeOut: false,
    
    // Smooth delta for consistent physics
    smoothStep: true
  },
  
  // Input configuration
  input: {
    // Enable touch
    touch: true,
    
    // Enable mouse
    mouse: true,
    
    // Enable keyboard
    keyboard: true,
    
    // Enable gamepad (optional)
    gamepad: false,
    
    // Active pointers (supports multi-touch but we only need 1)
    activePointers: 2
  },
  
  // Audio configuration
  audio: {
    // Disable audio when not in focus
    disableWebAudio: false,
    
    // Don't force audio unlock (let Phaser handle it)
    noAudio: false
  },
  
  // Banner settings (disable in production)
  banner: false,
  
  // Disable right-click context menu
  disableContextMenu: true,
  
  // DOM element configuration
  dom: {
    createContainer: false
  },
  
  // Callback when game loses focus
  callbacks: {
    preBoot: (_game: Phaser.Game) => {
      // Log game initialization
      console.log(`[${new Date().toISOString()}] Sticky Planet initializing...`);
      console.log(`[Config] Viewport: ${window.innerWidth}x${window.innerHeight}`);
      console.log(`[Config] Mode: ${window.innerWidth > window.innerHeight ? 'LANDSCAPE' : 'PORTRAIT'}`);
    },
    postBoot: (_game: Phaser.Game) => {
      // Log successful boot
      console.log(`[${new Date().toISOString()}] Sticky Planet ready!`);
    }
  }
};

export default GameConfig;
